import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);

// Storage keys — segregados: `sofi_token` es el token vivo (real o impersonado); si hay
// impersonación activa, `sofi_admin_token_backup` guarda el token original del admin.
const TOKEN_KEY = "sofi_token";
const ADMIN_BACKUP_KEY = "sofi_admin_token_backup";

export function AuthProvider({ children }) {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [me, setMe] = useState(null); // { user, modules, navigation, impersonation? }
  const [loading, setLoading] = useState(true);

  const fetchMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setMe(data);
      return data;
    } catch (e) {
      setMe(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (token) {
      fetchMe();
    } else {
      setMe(null);
      setLoading(false);
    }
  }, [token, fetchMe]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    // Al hacer login limpio siempre cualquier impersonación previa por seguridad.
    localStorage.removeItem(ADMIN_BACKUP_KEY);
    localStorage.setItem(TOKEN_KEY, data.access_token);
    setToken(data.access_token);
    return data;
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(ADMIN_BACKUP_KEY);
    setToken(null);
    setMe(null);
  };

  // ------------------- Impersonación (admin → user, read-only) -------------------
  // Guarda el token del admin en un slot separado y activa el token impersonado como principal.
  // Recarga la app para que TODA la UI (auth context, react-query cache, sidebar) reevalúe
  // desde cero con la nueva identidad.
  const startImpersonation = async (userId) => {
    const { data } = await api.post(`/admin/impersonate/${userId}`);
    const currentAdminToken = localStorage.getItem(TOKEN_KEY);
    if (currentAdminToken) localStorage.setItem(ADMIN_BACKUP_KEY, currentAdminToken);
    localStorage.setItem(TOKEN_KEY, data.access_token);
    window.location.href = "/app"; // recarga completa desde el dashboard
  };

  const stopImpersonation = async () => {
    // Auditoría del cierre. Best-effort: si falla igual salimos limpiamente en el cliente.
    try { await api.post("/admin/impersonate/end"); } catch (e) { /* noop */ }
    const backup = localStorage.getItem(ADMIN_BACKUP_KEY);
    if (backup) {
      localStorage.setItem(TOKEN_KEY, backup);
      localStorage.removeItem(ADMIN_BACKUP_KEY);
      window.location.href = "/app";
    } else {
      // Si no hay backup (edge case), forzamos logout limpio.
      logout();
      window.location.href = "/login";
    }
  };

  const impersonation = me?.impersonation || null;

  return (
    <AuthContext.Provider
      value={{
        token,
        me,
        user: me?.user || null,
        navigation: me?.navigation || [],
        modules: me?.modules || [],
        loading,
        login,
        logout,
        refreshMe: fetchMe,
        isAuthenticated: !!token,
        // Impersonación
        impersonation,                       // { active, impersonated_by, read_only } | null
        isImpersonating: !!impersonation?.active,
        startImpersonation,
        stopImpersonation,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
